// Triggers on the board (qa-runs#1426): the DM's "Set a trigger here" form, opened from the board's menu, the notice that
// movement is paused, and "Fire or ignore?". The form makes the trigger's area from the clicked square (or uses an area
// already painted there) and a cue (`shared/src/state/triggers.ts`) that stops movement, starts a fight, or both; the
// server cuts a move on the area's first square and holds the token. The pause notice: the DM's names the held tokens,
// each with Release, and Release all; a player's says only "Movement paused by the DM" while one of their own tokens is
// held. "Fire or ignore?" shows the DM the first trigger waiting, by its name and the token's, and how many more wait.
// All are read from the state, so a reload keeps them. Slice 3: a trigger's effects in words (`triggerWords`, the Cues
// list's and Test it's), and What's here: the triggers on a square and the hidden things standing on it.
import { useEffect, useState } from 'react';
import { boardExtent, footprintSquares, spanTest, TRIGGER_LIMITS, type Cue, type Grid, type Zone } from '@dragons-table/vtt-shared';
import { useBoardMap } from './sceneMap';
import { listCues } from '../sound';
import { placeKinds } from '../place';
import { useTableState } from '../table/TableState';
import { useMayAct } from '../table/mayAct';
import { useShell } from '../shell/context';
import { item } from './TokenMenu';

type Cell = { i: number; j: number };
type Token = { id: string; name: string; owners: string[]; hidden?: boolean; entry?: { segment: string }; x: number; y: number; size?: { w: number; h: number } };
let opened: Cell | null = null;
const listeners = new Set<(c: Cell | null) => void>();
/** Open the form for a square, or (null) close it. */
export const triggerForm = {
  open(c: Cell | null) { opened = c; for (const l of listeners) l(c); },
  subscribe(l: (c: Cell | null) => void) { listeners.add(l); return () => { listeners.delete(l); }; },
};

const btn = 'rounded border border-stone-600 px-2 py-1 text-sm hover:bg-stone-700 disabled:opacity-50';
const tokensOf = (state: unknown): Token[] => Object.values((state as { objects?: Record<string, Token> }).objects ?? {});

export function TriggerForm() {
  const { act, store, role, viewingAs, setNotice } = useTableState();
  const mayAct = useMayAct();
  const [cell, setCell] = useState<Cell | null>(opened);
  const [name, setName] = useState('');
  const [stop, setStop] = useState<'none' | 'token' | 'all'>('token');
  const [who, setWho] = useState<string>('any');
  const [every, setEvery] = useState<'session' | 'always' | 'ever'>('session');
  const [area, setArea] = useState<string>('new');
  const [fight, setFight] = useState<string[]>([]);
  const [ask, setAsk] = useState(true);
  const [on, setOn] = useState(true);
  // Slice 3: the trap it springs, one of the hidden markers on the scene (a trap placed from the compendium).
  const [trap, setTrap] = useState('');
  const [markers, setMarkers] = useState<string[]>([]);
  const { campaignId } = useShell();
  useEffect(() => triggerForm.subscribe((c) => { setCell(c); setArea('new'); setFight([]); setName(''); setTrap(''); }), []);
  useEffect(() => { if (cell && role === 'dm') void placeKinds(campaignId).then((k) => setMarkers(k.markers)).catch(() => undefined); }, [cell, campaignId, role]);
  if (!cell || role !== 'dm' || viewingAs || !mayAct('cue.set')) return null;
  const zones = ((store.state as { zones?: Zone[] }).zones ?? []).filter((z) => spanTest(z.spans)(cell));
  const tokens = tokensOf(store.state).sort((a, b) => a.name.localeCompare(b.name));
  const foes = tokens.filter((o) => !o.owners.length);
  const traps = tokens.filter((o) => o.hidden && o.entry && markers.includes(o.entry.segment));
  const empty = stop === 'none' && !fight.length && !trap;
  const save = () => {
    let zoneId = area;
    if (area === 'new') {
      zoneId = crypto.randomUUID();
      act({ kind: 'zone.paint', id: zoneId, zone: 'sound', label: name.trim() || 'Trigger', rect: { i0: cell.i, j0: cell.j, i1: cell.i, j1: cell.j } }, (r) => { if (!r.ok) setNotice(r.reason ?? 'The trigger\'s area could not be painted.'); });
    }
    const cue = {
      id: crypto.randomUUID(), trigger: { on: 'area_enter', zoneId }, channel: 'effect',
      ...(name.trim() ? { name: name.trim() } : {}), ...(stop !== 'none' ? { stop } : {}), ...(who !== 'any' ? { who } : {}),
      ...(every !== 'session' ? { every } : {}), ...(trap ? { trap } : {}), ...(fight.length ? { fight } : {}), ...((fight.length || trap) && !ask ? { auto: true } : {}), ...(!on ? { off: true } : {}),
    };
    act({ kind: 'cue.set', cue } as never, (r) => setNotice(r.ok ? 'Trigger set. Paint more of its area with the zone tool.' : r.reason ?? 'The trigger could not be set.'));
    triggerForm.open(null);
  };
  const row = 'flex flex-col gap-1 text-sm';
  return (
    <div role="dialog" aria-label="Set a trigger here" data-testid="trigger-form" className="fixed left-1/2 top-24 z-50 max-h-[80vh] w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto vtt-popover space-y-3 rounded-lg p-4 shadow-xl">
      <h2 className="text-base font-semibold">Set a trigger here</h2>
      <label className={row}>Name
        <input value={name} maxLength={TRIGGER_LIMITS.name} placeholder="Trigger" onChange={(e) => setName(e.target.value)} data-testid="trigger-name" />
      </label>
      <label className={row}>Area
        <select value={area} onChange={(e) => setArea(e.target.value)} data-testid="trigger-area">
          <option value="new">This square (paint more later)</option>
          {zones.map((z) => <option key={z.id} value={z.id}>{z.label ?? 'The area already here'}</option>)}
        </select>
      </label>
      <label className={row}>When a token walks in
        <select value={stop} onChange={(e) => setStop(e.target.value as 'none' | 'token' | 'all')} data-testid="trigger-stop">
          <option value="token">Stop that token</option>
          <option value="all">Stop all tokens</option>
          <option value="none">Don&apos;t stop movement</option>
        </select>
      </label>
      <label className={row}>Spring a trap
        <select value={trap} onChange={(e) => setTrap(e.target.value)} data-testid="trigger-trap">
          <option value="">No trap</option>
          {traps.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {!traps.length && <span className="opacity-70">Place a trap from the Compendium first: it stands hidden on the board.</span>}
      </label>
      <fieldset className={row} data-testid="trigger-fight">
        <legend>Start a fight with</legend>
        {foes.length ? foes.map((o) => (
          <label key={o.id} className="flex items-center gap-2">
            <input type="checkbox" checked={fight.includes(o.id)} disabled={!fight.includes(o.id) && fight.length >= TRIGGER_LIMITS.fight}
              onChange={(e) => setFight(e.target.checked ? [...fight, o.id] : fight.filter((x) => x !== o.id))} />
            {o.name}{o.hidden ? ' (hidden)' : ''}
          </label>
        )) : <span className="opacity-70">No monsters on this scene.</span>}
        {fight.length > 0 && <span className="opacity-70">Every player token joins too.</span>}
      </fieldset>
      {(fight.length > 0 || !!trap) && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={ask} onChange={(e) => setAsk(e.target.checked)} data-testid="trigger-ask" />Ask me first (Fire or ignore?)
        </label>
      )}
      <label className={row}>Who sets it off
        <select value={who} onChange={(e) => setWho(e.target.value)} data-testid="trigger-who">
          <option value="any">Any token</option>
          <option value="players">Player tokens only</option>
          {tokens.map((o) => <option key={o.id} value={o.id}>Only {o.name}</option>)}
        </select>
      </label>
      <label className={row}>How often
        <select value={every} onChange={(e) => setEvery(e.target.value as 'session' | 'always' | 'ever')} data-testid="trigger-every">
          <option value="session">Once per session</option>
          <option value="always">Every time</option>
          <option value="ever">Once ever</option>
        </select>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} data-testid="trigger-on" />On
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className={btn} onClick={() => triggerForm.open(null)}>Cancel</button>
        <button type="button" className={btn} data-testid="trigger-save" disabled={empty} onClick={save}>Set trigger</button>
      </div>
    </div>
  );
}

/** The pause notice: the DM's names the held tokens with Release each and Release all; a player's gives no reason. */
export function MovePausedNotice() {
  const { act, store, role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  const [open, setOpen] = useState(false);
  const held = (store.state as { paused?: string[] }).paused ?? [];
  if (!held.length) return null;
  const dm = role === 'dm' && !viewingAs && mayAct('move.release');
  const objects = (store.state as { objects?: Record<string, Token> }).objects ?? {};
  return (
    <div role="status" data-testid="move-paused" className="pointer-events-auto fixed left-1/2 top-3 z-40 -translate-x-1/2 vtt-popover rounded-2xl px-3 py-1 text-sm shadow">
      <div className="flex items-center gap-2">
        {dm ? <button type="button" className="underline" aria-expanded={open} onClick={() => setOpen(!open)}>Movement paused: {held.length === 1 ? '1 token' : `${held.length} tokens`}</button> : <span>Movement paused by the DM</span>}
        {dm && <button type="button" className={btn} data-testid="move-release" onClick={() => act({ kind: 'move.release' })}>{held.length === 1 ? 'Release' : 'Release all'}</button>}
      </div>
      {dm && open && held.length > 0 && (
        <ul className="mt-1 max-h-48 space-y-1 overflow-y-auto" data-testid="move-held">
          {held.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2">
              <span>{objects[id]?.name ?? 'A token'}</span>
              <button type="button" className={btn} data-testid="move-release-one" onClick={() => act({ kind: 'move.release', ids: [id] })}>Release</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** "Fire or ignore?": the DM's, the first trigger waiting by its name and the token's; the rest wait in order. */
export function FireOrIgnore() {
  const { act, store, role, viewingAs, setNotice } = useTableState();
  const { campaignId } = useShell();
  const mayAct = useMayAct();
  const waiting = (store.state as { waiting?: Array<{ cue: string; token: string }> }).waiting ?? [];
  const [cues, setCues] = useState<Cue[]>([]);
  const first = waiting[0];
  useEffect(() => { if (first && role === 'dm') void listCues(campaignId).then((got) => { if (got) setCues(got); }); }, [first?.cue, campaignId, role]);
  if (!first || role !== 'dm' || viewingAs || !mayAct('trigger.answer')) return null;
  const objects = (store.state as { objects?: Record<string, Token> }).objects ?? {};
  const cue = cues.find((c) => c.id === first.cue);
  const answer = (fire: boolean) => act({ kind: 'trigger.answer', cue: first.cue, token: first.token, fire }, (r) => { if (!r.ok) setNotice(r.reason ?? 'That answer did not go through.'); });
  return (
    <div role="alertdialog" aria-label="Fire or ignore?" data-testid="trigger-ask-dm" className="pointer-events-auto fixed left-1/2 top-14 z-40 -translate-x-1/2 vtt-popover space-y-2 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p><strong>{objects[first.token]?.name ?? 'A token'}</strong> set off <strong>{cue?.name ?? 'a trigger'}</strong>{cue?.trap ? ' (springs a trap)' : ''}{cue?.fight ? ' (starts a fight)' : ''}. Fire or ignore?</p>
      <div className="flex items-center justify-end gap-2">
        {waiting.length > 1 && <span className="mr-auto opacity-70">{waiting.length - 1} more waiting</span>}
        <button type="button" className={btn} data-testid="trigger-ignore" onClick={() => answer(false)}>Ignore</button>
        <button type="button" className={btn} data-testid="trigger-fire" onClick={() => answer(true)}>Fire</button>
      </div>
    </div>
  );
}

/**
 * A trigger's effects in words, in the order they run (stop movement, the sound, the line, the trap, the fight), and how it
 * fires: who sets it off, whether it asks first, how often, and whether it is off. `middle`: the sound's and the line's.
 */
export function triggerWords(c: Cue, nameOf: (id: string) => string, middle: string[] = []): { does: string; how: string } {
  const does = [
    c.stop === 'all' ? 'stops all tokens' : c.stop === 'token' ? 'stops that token' : '', ...middle,
    c.trap ? `springs ${nameOf(c.trap)}` : '',
    c.fight ? `starts a fight with ${[...c.fight.map(nameOf), 'every player token'].join(', ')}` : '',
  ].filter(Boolean).join('; ');
  const how = [
    !c.who ? '' : c.who === 'players' ? 'player tokens only' : `only ${nameOf(c.who)}`,
    c.fight || c.trap ? (c.auto ? 'fires on its own' : 'asks you first') : '',
    c.trigger.on === 'area_enter' && (c.stop || c.fight || c.trap) ? (c.every === 'always' ? 'every time' : c.every === 'ever' ? 'once ever' : 'once a session') : '',
    c.off ? 'off' : '',
  ].filter(Boolean).join(', ');
  return { does, how };
}

/** Test it (the issue's Defaults): what the trigger would do if a token walked in now, in words; nothing is changed. */
export function testWords(c: Cue, nameOf: (id: string) => string, middle: string[] = []): string {
  const { does } = triggerWords(c, nameOf, middle);
  if (c.off) return `${c.name ?? 'This trigger'} is switched off: nothing would fire. Nothing was changed.`;
  return `If a token walked in now, ${c.name ?? 'this trigger'} would: ${does || 'do nothing'}.${(c.fight || c.trap) && !c.auto ? ' It would ask you first.' : ''} Nothing was changed.`;
}

/** Switch a trigger on or off (the rogue disarmed it): the cue set again with `off` changed. */
export function switchedCue(c: Cue, on: boolean): Cue {
  const { by: _by, off: _off, ...rest } = c;
  return { ...rest, ...(on ? {} : { off: true as const }) };
}

let whatAt: Cell | null = null;
const whatListeners = new Set<(c: Cell | null) => void>();
/** Open What's here for a square, or (null) close it. */
export const whatsHere = {
  open(c: Cell | null) { whatAt = c; for (const l of whatListeners) l(c); },
  subscribe(l: (c: Cell | null) => void) { whatListeners.add(l); return () => { whatListeners.delete(l); }; },
};

/** What's here (the issue's Defaults): the DM's list of the triggers whose area covers a square and the hidden things on it. */
export function WhatsHere() {
  const { act, store, role, viewingAs } = useTableState();
  const { campaignId } = useShell();
  const mayAct = useMayAct();
  const map = useBoardMap();
  const [cell, setCell] = useState<Cell | null>(whatAt);
  const [cues, setCues] = useState<Cue[]>([]);
  const [tested, setTested] = useState('');
  const reload = () => void listCues(campaignId).then((got) => { if (got) setCues(got); });
  useEffect(() => whatsHere.subscribe((c) => { setCell(c); setTested(''); }), []);
  useEffect(() => { if (cell && role === 'dm') reload(); }, [cell, campaignId, role]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!cell || role !== 'dm' || viewingAs) return null;
  const objects = (store.state as { objects?: Record<string, Token> }).objects ?? {};
  const nameOf = (id: string) => objects[id]?.name ?? 'a token not on this scene';
  const zones = ((store.state as { zones?: Zone[] }).zones ?? []).filter((z) => spanTest(z.spans)(cell));
  const here = cues.filter((c) => c.trigger.on === 'area_enter' && zones.some((z) => z.id === (c.trigger as { zoneId: string }).zoneId));
  const grid = (store.state as { grid?: Grid }).grid;
  const extent = boardExtent(map);
  const hidden = grid ? Object.values(objects).filter((o) => {
    if (!o.hidden) return false;
    const b = footprintSquares(grid, extent, { x: o.x, y: o.y }, o.size ?? { w: 1, h: 1 });
    return cell.i >= b.i0 && cell.i <= b.i1 && cell.j >= b.j0 && cell.j <= b.j1;
  }) : [];
  const may = mayAct('cue.set');
  return (
    <div role="dialog" aria-label="What's here" data-testid="whats-here" className="fixed left-1/2 top-24 z-50 max-h-[80vh] w-[min(24rem,calc(100vw-2rem))] -translate-x-1/2 overflow-y-auto vtt-popover space-y-2 rounded-lg p-4 text-sm shadow-xl">
      <h2 className="text-base font-semibold">What&apos;s here</h2>
      <h3 className="font-medium">Triggers</h3>
      {here.length ? (
        <ul className="space-y-1" data-testid="whats-here-triggers">
          {here.map((c) => {
            const w = triggerWords(c, nameOf, [c.trackId ? 'plays a sound' : '', c.say ? 'says a line' : '']);
            return (
              <li key={c.id} className="space-y-1 rounded border border-stone-700 p-1">
                <div><strong>{c.name ?? 'Trigger'}</strong>: {w.does || 'nothing'}{w.how ? ` (${w.how})` : ''}</div>
                {may && (
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-1"><input type="checkbox" checked={!c.off} onChange={(e) => act({ kind: 'cue.set', cue: switchedCue(c, e.target.checked) } as never, () => reload())} />On</label>
                    <button type="button" className={btn} onClick={() => setTested(testWords(c, nameOf, [c.trackId ? 'play a sound' : '', c.say ? 'say a line' : '']))}>Test it</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : <p className="opacity-70">No triggers on this square.</p>}
      {tested && <p role="status" data-testid="whats-here-test">{tested}</p>}
      <h3 className="font-medium">Hidden things</h3>
      {hidden.length ? <ul data-testid="whats-here-hidden">{hidden.map((o) => <li key={o.id}>{o.name}</li>)}</ul> : <p className="opacity-70">Nothing hidden on this square.</p>}
      <div className="flex justify-end"><button type="button" className={btn} onClick={() => whatsHere.open(null)}>Close</button></div>
    </div>
  );
}

/** The DM's board-menu items: Set a trigger here, Pause all movement, and What's here. */
export function TriggerMenuItems({ cell, run }: { cell: Cell; run: (fn: () => void) => () => void }) {
  const { act, role, viewingAs } = useTableState();
  const mayAct = useMayAct();
  if (role !== 'dm' || viewingAs) return null;
  return (
    <>
      {mayAct('cue.set') && mayAct('zone.paint') && <button type="button" role="menuitem" className={item} data-testid="board-menu-trigger" onClick={run(() => triggerForm.open(cell))}>Set a trigger here</button>}
      {mayAct('move.pause') && <button type="button" role="menuitem" className={item} data-testid="board-menu-pause" onClick={run(() => act({ kind: 'move.pause', all: true }))}>Pause all movement</button>}
      <button type="button" role="menuitem" className={item} data-testid="board-menu-whats-here" onClick={run(() => whatsHere.open(cell))}>What&apos;s here</button>
    </>
  );
}
